import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	Grid,
	InputAdornment,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Switch,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import axios from 'axios';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetList, useNotify } from 'react-admin';
import { FaCopy, FaHistory, FaLightbulb, FaPlus, FaSyncAlt, FaUsers } from 'react-icons/fa';
import constants from '../../constants';
import SupportCircleSuggestionsDialog from './SupportCircleSuggestionsDialog';
import SupportCircleTimeline from './SupportCircleTimeline';
import { cancelReasonLabel, Currency, currencyOf, currencySymbol, fmtMoney, FreeFor, freeForCompanyIds, parseEmails, PaymentChip, PaymentStatus, PriceCell, refundFailuresOf, revenueOf } from './supportCirclePricing';

// GH-704 — "מעגלי תמיכה" admin: list / create / edit circles, open-close-cancel, registrations with
// the REAL identity next to the pseudonym (admin only — members and facilitators only ever see pseudonyms).

type Locale = { he?: string; en?: string };
type Status = 'draft' | 'open' | 'live' | 'ended' | 'cancelled';
interface Circle {
	_id: string;
	title: Locale;
	topic?: Locale;
	description?: Locale;
	rules?: Locale;
	facilitator?: { _id: string; name?: string } | null;
	facilitator_type: 'human' | 'ai';
	scheduled_at: string;
	duration_minutes: number;
	max_participants: number;
	status: Status;
	room_name: string;
	companies?: { _id: string; english_name?: string }[];
	excluded_companies?: { _id: string; english_name?: string }[]; // GH-708
	is_global: boolean;
	audio_only?: boolean; // GH-708
	agent_observer?: boolean; // GH-709
	audience?: Audience; // GH-708
	image?: string;
	started_at?: string;
	ended_at?: string;
	facilitator_joined_at?: string; // GH-841: from the LiveKit webhook — first time the facilitator entered
	facilitator_left_at?: string;
	// GH-841 stage B: facilitator invite + reminders (per-circle markers set by the notifications engine)
	facilitator_notified?: { facilitator?: string; scheduled_at?: string; at?: string } | null;
	facilitator_24h_at?: string;
	facilitator_2h_at?: string;
	registered_count?: number;
	joined_count?: number;
	// GH-841 stage C: paid circles — price in major units (0/empty = free), currency derived from is_global
	price?: number | null;
	free_for?: FreeFor | null;
	paid_count?: number; // adminList
	pending_count?: number; // adminList: seats held right now while the member pays (already inside registered_count)
	revenue?: number | { amount?: number; currency?: string } | null; // adminList: sum of paid amounts
	refund_failed_count?: number; // GH-841 adminList: refunds that need a manual PayMe refund (incl. earlier ones)
	currency?: Currency;
	is_test?: boolean; // GH-841 logs: a QA / rehearsal circle — its log rows carry is_test_circle and stay out of the KPIs
}
// GH-841 stage B: which member notifications went out (claimed atomically by the engine, one Date per step)
type NotifiedKey = 'registered_at' | 'confirm_48h_at' | 'reminder_24h_at' | 'reminder_2h_at' | 'reminder_1h_at' | 'reminder_10m_at' | 'reminder_30m_at' | 'feedback_at' | 'cancelled_at';
type Notified = Partial<Record<NotifiedKey, string>>;
interface Participant {
	_id: string;
	display_name: string;
	member?: { _id: string; first_name?: string; last_name?: string; email?: string; company?: string };
	registered_at?: string;
	cancelled_at?: string;
	joined_at?: string;
	left_at?: string;
	reason?: string; // GH-841: "מה הביא אותך למעגל?" (optional)
	share_reason?: boolean; // GH-841: ok to share the reason anonymously before the session
	feedback?: { rating?: number; comfort?: number; comment?: string; submitted_at?: string };
	confirmed_at?: string; // GH-841 stage B: tapped the 48h "confirm attendance" link (informational — never cancels)
	notified?: Notified;
	// GH-841 stage C: payment (pending = spot held for 15 min while the member pays; cancelled 'payment_expired' after)
	payment_status?: PaymentStatus | string;
	payment_id?: string;
	amount?: number;
	currency?: Currency;
	paid_at?: string;
	refunded_at?: string;
	hold_expires_at?: string;
	cancel_reason?: string;
	// GH-841: earlier payments on this registration (a refunded / failed refund before a re-registration, a refused duplicate)
	past_payments?: { payment_id?: string; amount?: number; currency?: string; status?: string; paid_at?: string; refunded_at?: string; note?: string }[];
}

const STATUS_LABELS: Record<Status, string> = { draft: 'טיוטה', open: 'פתוח להרשמה', live: 'מתקיים', ended: 'הסתיים', cancelled: 'בוטל' };
const STATUS_COLORS: Record<Status, 'default' | 'success' | 'warning' | 'error' | 'info'> = { draft: 'default', open: 'success', live: 'error', ended: 'info', cancelled: 'default' };
const API = `${constants.API_URL}/support-circles/admin`;
// GH-841 stage B: short labels for the "הודעות" chips, in send order
const NOTIFIED_LABELS: { key: NotifiedKey; label: string }[] = [
	{ key: 'registered_at', label: 'הרשמה' },
	{ key: 'confirm_48h_at', label: '48ש׳' },
	{ key: 'reminder_24h_at', label: '24ש׳' },
	{ key: 'reminder_2h_at', label: 'שעתיים' },
	// GH-878: WhatsApp an hour + 10 minutes before (the 30-minute one before the change)
	{ key: 'reminder_1h_at', label: 'שעה' },
	{ key: 'reminder_10m_at', label: '10 דק׳' },
	{ key: 'reminder_30m_at', label: 'חצי שעה' },
	{ key: 'feedback_at', label: 'משוב' },
	{ key: 'cancelled_at', label: 'ביטול' },
];

const emptyForm = () => ({
	title: { he: '', en: '' },
	topic: { he: '', en: '' },
	description: { he: '', en: '' },
	rules: { he: '1. מקשיבים בלי לקטוע.\n2. לא נותנים עצות אלא אם ביקשו.\n3. מה שנאמר במעגל נשאר במעגל.', en: '1. Listen without interrupting.\n2. No advice unless asked.\n3. What is said here stays here.' },
	facilitator: '' as string,
	facilitator_type: 'human' as 'human' | 'ai',
	scheduled_at: moment().add(1, 'day').hour(20).minute(0).format('YYYY-MM-DDTHH:mm'),
	duration_minutes: 60,
	max_participants: 10,
	companies: [] as string[],
	excluded_companies: [] as string[],
	is_global: false,
	audio_only: false,
	agent_observer: false,
	audience: { gender: null, parenting: null, languages: [] as string[], marital_status: [] as string[], caregiving: null } as Audience,
	status: 'draft' as Status,
	image: '',
	price: '' as string, // GH-841 stage C: the input's text — empty = free
	free_for_companies: [] as string[],
	free_for_emails: '', // textarea: one per line or comma-separated
	is_test: false, // GH-841 logs
});
type Form = ReturnType<typeof emptyForm>;
// GH-708: audience filters — every empty field = no restriction
type Audience = { gender?: 'M' | 'F' | null; parenting?: boolean | null; languages?: string[]; marital_status?: string[]; caregiving?: boolean | null };
const emptyAudience = (): Audience => ({ gender: null, parenting: null, languages: [], marital_status: [], caregiving: null });
const MARITAL_OPTIONS = [
	{ id: 'single', label: 'רווק/ה' },
	{ id: 'married', label: 'נשוי/אה' },
	{ id: 'divorced', label: 'גרוש/ה' },
	{ id: 'widowed', label: 'אלמן/ה' },
	{ id: 'relationship', label: 'בזוגיות' },
];

// GH-841 stage B: "נשלח זימון למנחה DD/MM" under the facilitator name. "(לפני שינוי)" when the invite went to
// another facilitator / another time (the engine re-sends only once the circle is 'open' again).
function FacilitatorInviteCaption({ circle: c }: { circle: Circle }) {
	const fn = c.facilitator_notified;
	if (!fn?.at) return null;
	const stale =
		(!!fn.facilitator && String(fn.facilitator) !== String(c.facilitator?._id || '')) ||
		(!!fn.scheduled_at && !moment(fn.scheduled_at).isSame(moment(c.scheduled_at)));
	const tip = [
		`זימון: ${moment(fn.at).format('DD/MM/YYYY HH:mm')}`,
		c.facilitator_24h_at ? `תזכורת 24ש׳: ${moment(c.facilitator_24h_at).format('DD/MM HH:mm')}` : '',
		c.facilitator_2h_at ? `תזכורת שעתיים: ${moment(c.facilitator_2h_at).format('DD/MM HH:mm')}` : '',
	]
		.filter(Boolean)
		.join(' · ');
	return (
		<Tooltip title={tip}>
			<Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'nowrap' }}>
				נשלח זימון למנחה {moment(fn.at).format('DD/MM')}
				{stale ? ' (לפני שינוי)' : ''}
			</Typography>
		</Tooltip>
	);
}

// GH-841 stage B: compact chips of the member notifications that went out (tooltip = send time)
function NotifiedChips({ notified }: { notified?: Notified }) {
	const sent = NOTIFIED_LABELS.filter(({ key }) => !!notified?.[key]);
	if (!sent.length) return <>—</>;
	return (
		<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 220 }}>
			{sent.map(({ key, label }) => (
				<Tooltip key={key} title={moment(notified?.[key]).format('DD/MM/YYYY HH:mm')}>
					<Chip size="small" variant="outlined" label={label} sx={{ height: 20, fontSize: 11 }} />
				</Tooltip>
			))}
		</Box>
	);
}

export default function SupportCirclesAdmin() {
	const notify = useNotify();
	const [circles, setCircles] = useState<Circle[]>([]);
	const [loading, setLoading] = useState(true);
	const [statusFilter, setStatusFilter] = useState<Status | 'all'>('all');
	// GH-841: global circles (international members, English) and Israel circles (company members) are two separate
	// worlds — a member only ever sees one of them — so the admin shows them in separate tabs.
	const [scope, setScope] = useState<'il' | 'global'>('il');
	const [form, setForm] = useState<Form | null>(null);
	const [editingId, setEditingId] = useState<string | null>(null);
	const [saving, setSaving] = useState(false);
	const [participantsOf, setParticipantsOf] = useState<{ circle: Circle; list: Participant[] } | null>(null);
	const [suggestionsOpen, setSuggestionsOpen] = useState(false); // GH-841 (GH-836)
	// GH-841 logs: "יומן" of a circle, or of one participant in it (opened from the participants dialog)
	const [timelineOf, setTimelineOf] = useState<{ circle: Circle; participantId?: string } | null>(null);
	// GH-709: transcript viewer
	type TranscriptLine = { at: string; identity: string; name: string; text: string };
	type TranscriptFlag = { at: string; name: string; kind: string; severity: number; note: string; quote: string };
	type Transcript = { started_at?: string; ended_at?: string; expires_at: string; lines: TranscriptLine[]; flags?: TranscriptFlag[]; summary?: string; clean_lines?: { name: string; text: string }[] } | null;
	const [transcriptOf, setTranscriptOf] = useState<{ circle: Circle; transcript: Transcript; loading: boolean } | null>(null);
	const showTranscript = async (c: Circle) => {
		setTranscriptOf({ circle: c, transcript: null, loading: true });
		try {
			const { data } = await axios.get(`${API}/${c._id}/transcript`);
			setTranscriptOf({ circle: c, transcript: data || null, loading: false });
		} catch {
			setTranscriptOf({ circle: c, transcript: null, loading: false });
		}
	};
	// the server assembles the file: summary + flagged moments + context-cleaned transcript
	const exportTranscript = async () => {
		if (!transcriptOf) return;
		try {
			const res = await axios.get(`${API}/${transcriptOf.circle._id}/transcript/export`, { responseType: 'blob' });
			const a = document.createElement('a');
			a.href = URL.createObjectURL(res.data);
			a.download = `circle-${transcriptOf.circle._id}.txt`;
			a.click();
			setTimeout(() => URL.revokeObjectURL(a.href), 5000);
		} catch {
			notify('הייצוא נכשל', { type: 'error' });
		}
	};
	const KIND_LABEL: Record<string, string> = { crisis: 'משבר', distress: 'מצוקה', conflict: 'עימות', silence: 'שתיקה' };

	const experts = useGetList('instructor/all', { pagination: { perPage: 2000, page: 1 }, sort: { field: 'name', order: 'ASC' } });
	const companies = useGetList('company', { pagination: { perPage: 1000, page: 1 }, sort: { field: 'english_name', order: 'ASC' } });
	// GH-841 stage C: an expert can facilitate only through an ACTIVE member account linked to them (expert_profile).
	// null = the list never loaded → nothing is disabled (never block the form over it). Re-fetched whenever the
	// form opens (an expert may have just been linked); a failed re-fetch keeps the last list.
	const [linkedExperts, setLinkedExperts] = useState<Set<string> | null>(null);
	const loadFacilitatorCandidates = useCallback(() => {
		axios
			.get(`${API}/facilitator-candidates`)
			.then(({ data }) => setLinkedExperts(new Set((data?.linked || []).map(String))))
			.catch(() => undefined);
	}, []);
	useEffect(() => {
		loadFacilitatorCandidates();
	}, [loadFacilitatorCandidates]);
	// the circle's current facilitator, so editing an old circle still shows them even if they're not in the experts list
	const [currentFacilitator, setCurrentFacilitator] = useState<{ id: string; name: string } | null>(null);
	const expertOptions = useMemo(() => {
		const opts = (experts.data || []).map((e: any) => ({ id: String(e.id), name: e.name || e.english_name || String(e.id) }));
		if (currentFacilitator && !opts.some((o) => o.id === currentFacilitator.id)) opts.push(currentFacilitator);
		return opts.map((o) => {
			const unlinked = !!linkedExperts && !linkedExperts.has(o.id);
			return { id: o.id, label: unlinked ? `${o.name} — אין משתמש` : o.name, disabled: unlinked };
		});
	}, [experts.data, linkedExperts, currentFacilitator]);
	const companyOptions = useMemo(() => (companies.data || []).map((c: any) => ({ id: String(c.id), label: c.english_name || String(c.id) })), [companies.data]);
	const [editingCircle, setEditingCircle] = useState<Circle | null>(null); // GH-841 stage C: for the "already paid" hint
	const [duplicatingId, setDuplicatingId] = useState<string | null>(null);
	const [cancellingId, setCancellingId] = useState<string | null>(null); // GH-856

	const load = useCallback(async () => {
		setLoading(true);
		try {
			const res = await axios.get(`${API}?limit=200`);
			setCircles(res.data?.docs || []);
		} catch (e: any) {
			notify(e?.response?.data?.message || 'טעינת המעגלים נכשלה', { type: 'error' });
		} finally {
			setLoading(false);
		}
	}, [notify]);
	useEffect(() => {
		load();
	}, [load]);

	const openCreate = () => {
		setEditingId(null);
		setEditingCircle(null);
		setCurrentFacilitator(null);
		loadFacilitatorCandidates();
		setForm({ ...emptyForm(), is_global: scope === 'global' }); // GH-841: a new circle starts in the open tab's world
	};
	const openEdit = (c: Circle) => {
		setEditingId(c._id);
		setEditingCircle(c);
		setCurrentFacilitator(c.facilitator?._id ? { id: String(c.facilitator._id), name: c.facilitator.name || String(c.facilitator._id) } : null);
		loadFacilitatorCandidates();
		setForm({
			title: { he: c.title?.he || '', en: c.title?.en || '' },
			topic: { he: c.topic?.he || '', en: c.topic?.en || '' },
			description: { he: c.description?.he || '', en: c.description?.en || '' },
			rules: { he: c.rules?.he || '', en: c.rules?.en || '' },
			facilitator: c.facilitator?._id || '',
			facilitator_type: c.facilitator_type || 'human',
			scheduled_at: moment(c.scheduled_at).format('YYYY-MM-DDTHH:mm'),
			duration_minutes: c.duration_minutes,
			max_participants: c.max_participants,
			companies: (c.companies || []).map((x) => x._id),
			excluded_companies: (c.excluded_companies || []).map((x) => x._id),
			is_global: !!c.is_global,
			audio_only: !!c.audio_only,
			agent_observer: !!c.agent_observer,
			audience: { ...emptyAudience(), ...(c.audience || {}) },
			status: c.status,
			image: c.image || '',
			price: c.price ? String(c.price) : '', // GH-841 stage C
			free_for_companies: freeForCompanyIds(c.free_for),
			free_for_emails: (c.free_for?.emails || []).join('\n'),
			is_test: !!c.is_test, // GH-841 logs
		});
	};
	const save = async () => {
		if (!form) return;
		if (!form.title.he && !form.title.en) return notify('חסרה כותרת', { type: 'warning' });
		// GH-841 stage C: price ≥ 0 (empty = free); free_for emails must all look like emails
		const priceText = String(form.price ?? '').trim();
		const price = priceText === '' ? 0 : Number(priceText);
		// whole numbers only, like the server (PayMe gets price × 100 and the refund is floored)
		if (!Number.isInteger(price) || price < 0) return notify('מחיר לא תקין — מספר שלם, 0 ומעלה (ריק = חינם)', { type: 'warning' });
		const freeEmails = parseEmails(form.free_for_emails);
		if (freeEmails.invalid.length) return notify(`אימייל לא תקין ב"חינם עבור": ${freeEmails.invalid.join(', ')}`, { type: 'warning' });
		const paidCount = editingCircle?.paid_count || 0;
		// eslint-disable-next-line no-alert
		if (form.status === 'cancelled' && editingCircle?.status !== 'cancelled' && paidCount > 0 && !window.confirm(`לבטל את המעגל? ${paidCount} משתתפים ששילמו יקבלו החזר מלא אוטומטית.`)) return;
		setSaving(true);
		try {
			const { free_for_companies, free_for_emails, ...rest } = form;
			const body = {
				...rest,
				facilitator: form.facilitator || null,
				scheduled_at: moment(form.scheduled_at).toDate(),
				duration_minutes: Number(form.duration_minutes),
				max_participants: Number(form.max_participants),
				image: form.image || undefined,
				price,
				free_for: { companies: free_for_companies, emails: freeEmails.emails },
			};
			const { data } = editingId ? await axios.put(`${API}/${editingId}`, body) : await axios.post(API, body);
			// GH-841 stage C: cancelling refunds the payers (awaited on the server) — a failed refund needs a manual one.
			// Count only the refunds of THIS cancel (cancel_reason 'circle_cancelled'), never an older failed self-cancel refund.
			const justCancelled = form.status === 'cancelled' && editingCircle?.status !== 'cancelled';
			const byCancel = justCancelled ? ((data?.participants || []) as Participant[]).filter((p) => p.cancel_reason === 'circle_cancelled') : [];
			const failed = byCancel.filter((p) => p.payment_status === 'refund_failed').length;
			const refunded = byCancel.filter((p) => p.payment_status === 'refunded').length;
			if (failed) notify(`המעגל בוטל · הוחזרו ${refunded} · ${failed} החזרים נכשלו — יש להחזיר ידנית ב-PayMe`, { type: 'warning' });
			else notify(`${editingId ? 'המעגל עודכן' : 'המעגל נוצר'}${refunded ? ` · הוחזרו ${refunded} תשלומים` : ''}`, { type: 'success' });
			setForm(null);
			await load();
		} catch (e: any) {
			notify(e?.response?.data?.message || 'השמירה נכשלה', { type: 'error' });
		} finally {
			setSaving(false);
		}
	};
	const setStatus = async (c: Circle, status: Status) => {
		// GH-841 stage C: cancelling refunds every paid participant automatically — say so before it happens
		// eslint-disable-next-line no-alert
		if (status === 'cancelled' && (c.paid_count || 0) > 0 && !window.confirm(`לבטל את "${c.title?.he || c.title?.en}"? ${c.paid_count} משתתפים ששילמו יקבלו החזר מלא אוטומטית.`)) return;
		try {
			const { data } = await axios.post(`${API}/${c._id}/status`, { status });
			// GH-841 stage C: { refunds: { refunded, refund_failed } } on the move to 'cancelled'
			const r = data?.refunds;
			if (r?.refund_failed) notify(`סטטוס: ${STATUS_LABELS[status]} · הוחזרו ${r.refunded} · ${r.refund_failed} החזרים נכשלו — יש להחזיר ידנית ב-PayMe`, { type: 'warning' });
			else notify(`סטטוס: ${STATUS_LABELS[status]}${r?.refunded ? ` · הוחזרו ${r.refunded} תשלומים` : ''}`, { type: 'success' });
			await load();
		} catch (e: any) {
			notify(e?.response?.data?.message || 'שינוי הסטטוס נכשל', { type: 'error' });
		}
	};
	// GH-841 stage C: copy of the circle as a draft a week later (all config, no participants / runtime markers),
	// then straight into its edit dialog
	const duplicate = async (c: Circle) => {
		setDuplicatingId(c._id);
		try {
			const { data } = await axios.post(`${API}/${c._id}/duplicate`);
			notify('שוכפל כטיוטה', { type: 'success' });
			if (statusFilter !== 'all' && statusFilter !== 'draft') setStatusFilter('all'); // so the new draft is visible after closing
			await load();
			if (data?._id) openEdit(data as Circle);
		} catch (e: any) {
			notify(e?.response?.data?.message || 'השכפול נכשל', { type: 'error' });
		} finally {
			setDuplicatingId(null);
		}
	};
	const remove = async (c: Circle) => {
		// eslint-disable-next-line no-alert
		if (!window.confirm(`למחוק את "${c.title?.he || c.title?.en}"? (אפשר רק למעגל בלי נרשמים)`)) return;
		try {
			await axios.delete(`${API}/${c._id}`);
			notify('נמחק', { type: 'success' });
			await load();
		} catch (e: any) {
			notify(e?.response?.data?.message || 'המחיקה נכשלה', { type: 'error' });
		}
	};
	const showParticipants = async (c: Circle) => {
		try {
			const res = await axios.get(`${API}/${c._id}`);
			setParticipantsOf({ circle: c, list: res.data?.participants || [] });
		} catch (e: any) {
			notify(e?.response?.data?.message || 'טעינת הנרשמים נכשלה', { type: 'error' });
		}
	};
	// GH-856: cancel ONE participant's registration when they ask — paid → full refund through PayMe (a failed refund
	// still cancels: refund it by hand). Nothing is sent to the participant.
	const cancelParticipant = async (c: Circle, p: Participant) => {
		const paid = p.payment_status === 'paid';
		const who = `${p.member?.first_name || ''} ${p.member?.last_name || ''}`.trim() || p.display_name;
		const money = fmtMoney(Number(p.amount) || 0, p.currency || currencyOf(c));
		// eslint-disable-next-line no-alert
		if (!window.confirm(`לבטל את ההרשמה של ${who} ("${p.display_name}")?${paid ? `\nיוחזרו ${money} בכרטיס, דרך PayMe.` : ''}\nלא תישלח הודעה למשתתף/ת.`)) return;
		setCancellingId(p._id);
		try {
			const { data } = await axios.post(`${API}/${c._id}/participants/${p._id}/cancel`);
			if (data?.refund === 'refund_failed') notify(`ההרשמה בוטלה · ההחזר נכשל — יש להחזיר ${money} ידנית ב-PayMe`, { type: 'warning' });
			else notify(`ההרשמה בוטלה${data?.refund === 'refunded' ? ` · הוחזרו ${money}` : ''}`, { type: 'success' });
		} catch (e: any) {
			notify(e?.response?.data?.message || 'הביטול נכשל', { type: 'error' });
		} finally {
			setCancellingId(null);
			await showParticipants(c);
			await load();
		}
	};

	const inScope = circles.filter((c) => (scope === 'global' ? !!c.is_global : !c.is_global));
	const visible = inScope.filter((c) => statusFilter === 'all' || c.status === statusFilter);
	const selectedFacilitator = form ? expertOptions.find((o) => o.id === form.facilitator) : undefined; // GH-841 stage C
	const siteBase = constants.API_URL.includes('127.0.0.1') || constants.API_URL.includes('localhost') ? 'http://localhost:8082' : 'https://web.grouphug.online';

	return (
		<Box p={2} dir="rtl">
			<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
				<Box>
					<Typography variant="h5">מעגלי תמיכה</Typography>
					<Typography variant="body2" color="text.secondary">
						שיחות וידאו קבוצתיות מונחות בתוך האפליקציה. המשתתפים מופיעים בשם בדוי; הזהות האמיתית נראית רק כאן.
					</Typography>
				</Box>
				<Stack direction="row" spacing={1}>
					<Button variant="outlined" startIcon={<FaLightbulb />} onClick={() => setSuggestionsOpen(true)}>
						הצעות נושאים
					</Button>
					<Button variant="outlined" startIcon={<FaSyncAlt />} onClick={load} disabled={loading}>
						רענון
					</Button>
					<Button variant="contained" startIcon={<FaPlus />} onClick={openCreate}>
						מעגל חדש
					</Button>
				</Stack>
			</Stack>

			<Paper sx={{ p: 2, mt: 2 }}>
				<Tabs value={scope} onChange={(_, v) => setScope(v)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
					<Tab value="il" label={`ישראל — עובדי חברות (${circles.filter((c) => !c.is_global).length})`} />
					<Tab value="global" label={`גלובלי (${circles.filter((c) => !!c.is_global).length})`} />
				</Tabs>
				<Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
					<Chip label={`הכל (${inScope.length})`} color={statusFilter === 'all' ? 'primary' : 'default'} onClick={() => setStatusFilter('all')} />
					{(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
						<Chip key={s} label={`${STATUS_LABELS[s]} (${inScope.filter((c) => c.status === s).length})`} color={statusFilter === s ? 'primary' : 'default'} onClick={() => setStatusFilter(s)} />
					))}
				</Stack>
				<TableContainer>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell align="right">מועד</TableCell>
								<TableCell align="right">כותרת</TableCell>
								<TableCell align="right">נושא</TableCell>
								<TableCell align="right">מנחה</TableCell>
								<TableCell align="right">סטטוס</TableCell>
								<TableCell align="right">נרשמים</TableCell>
								<TableCell align="right">מחיר</TableCell>
								<TableCell align="right">שילמו · הכנסה</TableCell>
								<TableCell align="right">נכנסו</TableCell>
								<TableCell align="right">המנחה נכנס/ה</TableCell>
								<TableCell align="right">קהל</TableCell>
								<TableCell align="right">פעולות</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{visible.map((c) => (
								<TableRow key={c._id} hover>
									<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
										{moment(c.scheduled_at).format('DD/MM/YYYY HH:mm')} · {c.duration_minutes} דק׳
									</TableCell>
									<TableCell align="right" sx={{ fontWeight: 600 }}>{c.title?.he || c.title?.en}</TableCell>
									<TableCell align="right">{c.topic?.he || c.topic?.en || '—'}</TableCell>
									<TableCell align="right">
										{c.facilitator?.name || <Typography variant="caption" color="text.secondary">צוות GroupHug (אדמין)</Typography>}{c.facilitator_type === 'ai' ? ' · AI' : ''}
										<FacilitatorInviteCaption circle={c} />
									</TableCell>
									<TableCell align="right">
										<Chip size="small" label={STATUS_LABELS[c.status]} color={STATUS_COLORS[c.status]} />
									</TableCell>
									<TableCell align="right">
										{c.registered_count ?? 0} / {c.max_participants}
										{/* GH-841 stage C: holds count toward the capacity until they expire */}
										{c.pending_count ? (
											<Typography variant="caption" color="text.secondary" display="block" sx={{ whiteSpace: 'nowrap' }}>
												כולל {c.pending_count} ממתינים לתשלום
											</Typography>
										) : null}
									</TableCell>
									{/* GH-841 stage C */}
									<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
										<PriceCell circle={c} />
									</TableCell>
									<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
										{c.paid_count ? `${c.paid_count} · ${fmtMoney(revenueOf(c).amount, revenueOf(c).currency)}` : '—'}
										{/* GH-841: money owed back by hand — never only inside the dialog */}
										{c.refund_failed_count ? (
											<Typography variant="caption" color="error" display="block" sx={{ whiteSpace: 'nowrap' }}>
												{c.refund_failed_count} החזרים נכשלו — להחזיר ידנית
											</Typography>
										) : null}
									</TableCell>
									<TableCell align="right">{c.joined_count ?? 0}</TableCell>
									{/* GH-841: did the expert show up? (LiveKit webhook) — red once the slot has started without them */}
									<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
										{c.facilitator_joined_at ? (
											`${moment(c.facilitator_joined_at).format('HH:mm')}${c.facilitator_left_at ? ` · יצא/ה ${moment(c.facilitator_left_at).format('HH:mm')}` : ''}`
										) : moment().isAfter(moment(c.scheduled_at)) && c.status !== 'cancelled' && c.status !== 'draft' ? (
											<Typography variant="caption" color="error">לא נכנס/ה</Typography>
										) : (
											'—'
										)}
									</TableCell>
									<TableCell align="right">
										{c.is_test ? <Chip size="small" color="secondary" variant="outlined" label="בדיקה" sx={{ ml: 0.5 }} /> : null}
										{c.is_global ? <Chip size="small" label="גלובלי" /> : null}
										{c.audio_only ? <Chip size="small" color="info" label="שמע בלבד" sx={{ ml: 0.5 }} /> : null}
										{c.agent_observer ? <Chip size="small" color="secondary" label="סוכן משקיף" sx={{ ml: 0.5 }} /> : null}
										{c.excluded_companies?.length ? <Chip size="small" variant="outlined" label={`חוץ מ: ${c.excluded_companies.map((x) => x.english_name).join(', ')}`} sx={{ ml: 0.5 }} /> : null}
										{c.companies?.length ? c.companies.map((x) => <Chip key={x._id} size="small" label={x.english_name} sx={{ ml: 0.5 }} />) : !c.is_global ? <Typography variant="caption" color="text.secondary">כל החברות</Typography> : null}
									</TableCell>
									<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
										<Stack direction="row" spacing={0.5} justifyContent="flex-start">
											<Button size="small" onClick={() => openEdit(c)}>עריכה</Button>
											{/* GH-841 stage C */}
											<Tooltip title="עותק כטיוטה, שבוע אחרי המועד הזה — כל ההגדרות, בלי נרשמים">
												<span>
													<Button size="small" startIcon={<FaCopy />} onClick={() => duplicate(c)} disabled={duplicatingId === c._id}>
														שכפול
													</Button>
												</span>
											</Tooltip>
											<Tooltip title="נרשמים, נוכחות ומשוב (זהות אמיתית)">
												<Button size="small" startIcon={<FaUsers />} onClick={() => showParticipants(c)}>
													נרשמים
												</Button>
											</Tooltip>
											{/* GH-841 logs */}
											<Tooltip title="יומן: הרשמות, תשלומים, הודעות ומסירתן, ניסיונות כניסה, ניתוקים, המנחה ופעולות אדמין">
												<Button size="small" startIcon={<FaHistory />} onClick={() => setTimelineOf({ circle: c })}>
													יומן
												</Button>
											</Tooltip>
											{c.status === 'draft' && <Button size="small" color="success" onClick={() => setStatus(c, 'open')}>פתיחה להרשמה</Button>}
											{(c.status === 'open' || c.status === 'live') && <Button size="small" color="warning" onClick={() => setStatus(c, 'ended')}>סיום</Button>}
											{c.status !== 'cancelled' && c.status !== 'ended' && <Button size="small" color="error" onClick={() => setStatus(c, 'cancelled')}>ביטול</Button>}
											{c.status === 'draft' && <Button size="small" color="error" onClick={() => remove(c)}>מחיקה</Button>}
											{c.agent_observer && (c.status === 'live' || c.status === 'ended') && (
												<Button size="small" onClick={() => showTranscript(c)}>תמלול</Button>
											)}
											<Button size="small" href={`${siteBase}/support-circles/${c._id}`} target="_blank" rel="noreferrer">
												באתר ↗
											</Button>
										</Stack>
									</TableCell>
								</TableRow>
							))}
							{!loading && visible.length === 0 && (
								<TableRow>
									<TableCell colSpan={12} align="center">
										<Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>אין מעגלים.</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>

			{/* create / edit */}
			<Dialog open={!!form} onClose={() => setForm(null)} maxWidth="md" fullWidth dir="rtl">
				<DialogTitle>{editingId ? 'עריכת מעגל' : 'מעגל חדש'}</DialogTitle>
				{form && (
					<DialogContent>
						<Grid container spacing={2} sx={{ pt: 1 }}>
							<Grid item xs={12} md={6}><TextField label="כותרת (עברית)" fullWidth value={form.title.he} onChange={(e) => setForm({ ...form, title: { ...form.title, he: e.target.value } })} /></Grid>
							<Grid item xs={12} md={6}><TextField label="Title (English)" fullWidth value={form.title.en} onChange={(e) => setForm({ ...form, title: { ...form.title, en: e.target.value } })} /></Grid>
							<Grid item xs={12} md={6}><TextField label="נושא (עברית)" fullWidth value={form.topic.he} onChange={(e) => setForm({ ...form, topic: { ...form.topic, he: e.target.value } })} /></Grid>
							<Grid item xs={12} md={6}><TextField label="Topic (English)" fullWidth value={form.topic.en} onChange={(e) => setForm({ ...form, topic: { ...form.topic, en: e.target.value } })} /></Grid>
							<Grid item xs={12} md={6}><TextField label="תיאור (עברית)" fullWidth multiline minRows={2} value={form.description.he} onChange={(e) => setForm({ ...form, description: { ...form.description, he: e.target.value } })} /></Grid>
							<Grid item xs={12} md={6}><TextField label="Description (English)" fullWidth multiline minRows={2} value={form.description.en} onChange={(e) => setForm({ ...form, description: { ...form.description, en: e.target.value } })} /></Grid>
							<Grid item xs={12} md={6}><TextField label="כללי המעגל (עברית)" fullWidth multiline minRows={3} value={form.rules.he} onChange={(e) => setForm({ ...form, rules: { ...form.rules, he: e.target.value } })} /></Grid>
							<Grid item xs={12} md={6}><TextField label="Rules (English)" fullWidth multiline minRows={3} value={form.rules.en} onChange={(e) => setForm({ ...form, rules: { ...form.rules, en: e.target.value } })} /></Grid>

							<Grid item xs={12} md={8}>
								{/* GH-841 stage C: experts without an active linked member account can't enter the room → disabled */}
								<Autocomplete
									options={expertOptions}
									value={selectedFacilitator || null}
									onChange={(_, v) => setForm({ ...form, facilitator: v?.id || '' })}
									getOptionDisabled={(o) => o.disabled}
									isOptionEqualToValue={(o, v) => o.id === v.id}
									renderInput={(p) => (
										<TextField
											{...p}
											label="מנחה (מומחה/ית). ריק = צוות GroupHug, כל אדמין יכול להנחות"
											helperText={
												selectedFacilitator?.disabled
													? 'למנחה הזה/ו אין משתמש מנוי פעיל — לא יוכל/תוכל להיכנס למעגל'
													: linkedExperts
													? 'מומחים בלי משתמש מנוי פעיל מסומנים "אין משתמש" ולא ניתנים לבחירה'
													: undefined
											}
											FormHelperTextProps={{ sx: { color: selectedFacilitator?.disabled ? 'error.main' : undefined } }}
										/>
									)}
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<FormControl fullWidth>
									<InputLabel>סוג מנחה</InputLabel>
									<Select value={form.facilitator_type} label="סוג מנחה" onChange={(e) => setForm({ ...form, facilitator_type: e.target.value as 'human' | 'ai' })}>
										<MenuItem value="human">אנושי</MenuItem>
										<MenuItem value="ai" disabled>AI (בהמשך)</MenuItem>
									</Select>
								</FormControl>
							</Grid>

							<Grid item xs={12} md={4}><TextField label="מועד" type="datetime-local" fullWidth InputLabelProps={{ shrink: true }} value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} /></Grid>
							<Grid item xs={6} md={4}><TextField label="אורך (דקות)" type="number" fullWidth value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: Number(e.target.value) })} /></Grid>
							<Grid item xs={6} md={4}><TextField label="מקסימום משתתפים" type="number" fullWidth value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: Number(e.target.value) })} /></Grid>

							<Grid item xs={12} md={8}>
								<Autocomplete
									multiple
									options={companyOptions}
									value={companyOptions.filter((o) => form.companies.includes(o.id))}
									onChange={(_, v) => setForm({ ...form, companies: v.map((x) => x.id) })}
									renderInput={(p) => <TextField {...p} label="חברות (ריק = פתוח לכל החברות)" />}
								/>
							</Grid>
							<Grid item xs={6} md={2}>
								<FormControlLabel control={<Switch checked={form.is_global} onChange={(e) => setForm({ ...form, is_global: e.target.checked })} />} label="גלובלי" />
							</Grid>
							{/* GH-708: "everyone except", no-camera, audience filters */}
							<Grid item xs={12} md={8}>
								<Autocomplete
									multiple
									options={companyOptions}
									value={companyOptions.filter((o) => form.excluded_companies.includes(o.id))}
									onChange={(_, v) => setForm({ ...form, excluded_companies: v.map((x) => x.id) })}
									renderInput={(p) => <TextField {...p} label="הסתר מחברות (ההסתרה גוברת על הבחירה למעלה)" />}
								/>
							</Grid>
							<Grid item xs={12} md={4}>
								<FormControlLabel control={<Switch checked={form.audio_only} onChange={(e) => setForm({ ...form, audio_only: e.target.checked })} />} label="בלי מצלמה (שמע בלבד)" />
							</Grid>
							<Grid item xs={12}>
								{/* GH-709: silent observer — dispatched when the facilitator starts; adds the consent line to the rules */}
								<FormControlLabel control={<Switch checked={form.agent_observer} onChange={(e) => setForm({ ...form, agent_observer: e.target.checked })} />} label="סוכן משקיף (תמלול AI; מוסיף שורת הסכמה לכללי המעגל)" />
							</Grid>
							<Grid item xs={12}>
								{/* GH-841 logs: changes nothing for members — only marks the circle's log rows (is_test_circle) so QA / rehearsal circles stay out of the KPIs */}
								<FormControlLabel
									control={<Checkbox checked={!!form.is_test} onChange={(e) => setForm({ ...form, is_test: e.target.checked })} />}
									label="מעגל בדיקה (QA / חזרה) — לא נספר במדדים ובדוחות. לא משנה כלום למשתתפים"
								/>
							</Grid>
							<Grid item xs={12}><Typography variant="subtitle2" color="text.secondary">קהל יעד (ריק = כולם)</Typography></Grid>
							<Grid item xs={6} md={2}>
								<FormControl fullWidth size="small">
									<InputLabel>מגדר</InputLabel>
									<Select value={form.audience.gender || ''} label="מגדר" onChange={(e) => setForm({ ...form, audience: { ...form.audience, gender: (e.target.value || null) as any } })}>
										<MenuItem value="">כולם</MenuItem>
										<MenuItem value="F">נשים</MenuItem>
										<MenuItem value="M">גברים</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={6} md={2}>
								<FormControlLabel control={<Switch checked={!!form.audience.parenting} onChange={(e) => setForm({ ...form, audience: { ...form.audience, parenting: e.target.checked ? true : null } })} />} label="הורים בלבד" />
							</Grid>
							<Grid item xs={6} md={2}>
								<FormControlLabel control={<Switch checked={!!form.audience.caregiving} onChange={(e) => setForm({ ...form, audience: { ...form.audience, caregiving: e.target.checked ? true : null } })} />} label="מטפלים בקרוב" />
							</Grid>
							<Grid item xs={6} md={2}>
								<FormControl fullWidth size="small">
									<InputLabel>שפה</InputLabel>
									<Select
										multiple
										value={form.audience.languages || []}
										label="שפה"
										onChange={(e) => setForm({ ...form, audience: { ...form.audience, languages: (typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value) as string[] } })}
									>
										<MenuItem value="he">עברית</MenuItem>
										<MenuItem value="en">אנגלית</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} md={4}>
								<Autocomplete
									multiple
									size="small"
									options={MARITAL_OPTIONS}
									value={MARITAL_OPTIONS.filter((o) => (form.audience.marital_status || []).includes(o.id))}
									onChange={(_, v) => setForm({ ...form, audience: { ...form.audience, marital_status: v.map((x) => x.id) } })}
									renderInput={(p) => <TextField {...p} label="מצב משפחתי" />}
								/>
							</Grid>
							<Grid item xs={6} md={2}>
								<FormControl fullWidth>
									<InputLabel>סטטוס</InputLabel>
									<Select value={form.status} label="סטטוס" onChange={(e) => setForm({ ...form, status: e.target.value as Status })}>
										{(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
											<MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>
										))}
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12}><TextField label="תמונה (URL, לא חובה)" fullWidth value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} /></Grid>

							{/* GH-841 stage C: price (registered members only; the server computes each member's price) */}
							<Grid item xs={12}><Typography variant="subtitle2" color="text.secondary">תשלום</Typography></Grid>
							<Grid item xs={12} md={4}>
								<TextField
									label="מחיר"
									type="number"
									fullWidth
									value={form.price}
									onChange={(e) => setForm({ ...form, price: e.target.value })}
									inputProps={{ min: 0, step: 1 }}
									InputProps={{ startAdornment: <InputAdornment position="start">{currencySymbol(currencyOf(form))}</InputAdornment> }}
									helperText={
										(editingCircle?.paid_count || 0) > 0
											? `ריק = חינם · ${editingCircle?.paid_count} כבר שילמו — שינוי המחיר לא משפיע עליהם`
											: `ריק = חינם · ${form.is_global ? 'דולר (מעגל גלובלי)' : 'שקלים'}`
									}
								/>
							</Grid>
							<Grid item xs={12} md={8}>
								<Autocomplete
									multiple
									options={companyOptions}
									value={companyOptions.filter((o) => form.free_for_companies.includes(o.id))}
									onChange={(_, v) => setForm({ ...form, free_for_companies: v.map((x) => x.id) })}
									renderInput={(p) => <TextField {...p} label="חינם עבור — חברות" helperText="עובדי החברות האלה לא משלמים" />}
								/>
							</Grid>
							<Grid item xs={12}>
								{(() => {
									const parsed = parseEmails(form.free_for_emails);
									return (
										<TextField
											label="חינם עבור — אימיילים"
											fullWidth
											multiline
											minRows={2}
											maxRows={8}
											value={form.free_for_emails}
											onChange={(e) => setForm({ ...form, free_for_emails: e.target.value })}
											error={parsed.invalid.length > 0}
											helperText={
												parsed.invalid.length
													? `לא תקין: ${parsed.invalid.join(', ')}`
													: `אחד בכל שורה או מופרדים בפסיק · נבדק גם מול אימייל פרטי מאומת${parsed.emails.length ? ` · ${parsed.emails.length} אימיילים` : ''}`
											}
											inputProps={{ dir: 'ltr' }}
										/>
									);
								})()}
							</Grid>
						</Grid>
					</DialogContent>
				)}
				<DialogActions>
					<Button onClick={() => setForm(null)}>ביטול</Button>
					<Button variant="contained" onClick={save} disabled={saving}>שמירה</Button>
				</DialogActions>
			</Dialog>

			{/* participants */}
			{/* GH-709: transcript */}
			<Dialog open={!!transcriptOf} onClose={() => setTranscriptOf(null)} maxWidth="md" fullWidth dir="rtl">
				<DialogTitle>תמלול — {transcriptOf?.circle.title?.he || transcriptOf?.circle.title?.en}</DialogTitle>
				<DialogContent>
					{transcriptOf?.loading ? (
						<Typography variant="body2" color="text.secondary">טוען…</Typography>
					) : !transcriptOf?.transcript || transcriptOf.transcript.lines.length === 0 ? (
						<Typography variant="body2" color="text.secondary">אין תמלול למעגל הזה (הסוכן לא היה בחדר, או שהתמלול נמחק אחרי 60 יום).</Typography>
					) : (
						<>
							<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
								<Typography variant="caption" color="text.secondary">
									{transcriptOf.transcript.lines.length} שורות · שמות בדויים בלבד · נמחק אוטומטית ב-{moment(transcriptOf.transcript.expires_at).format('D.M.YYYY')}
								</Typography>
								<Button size="small" variant="outlined" onClick={exportTranscript}>ייצוא לקובץ טקסט</Button>
							</Stack>
							{!!transcriptOf.transcript.summary && (
								<Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#f4f7fb', borderRadius: 1, fontSize: 14, lineHeight: 1.7 }}>
									<Typography variant="subtitle2" sx={{ mb: 0.5 }}>סיכום</Typography>
									{transcriptOf.transcript.summary}
								</Box>
							)}
							{!!transcriptOf.transcript.flags?.length && (
								<Box sx={{ mb: 1.5, p: 1.5, bgcolor: '#fff7ed', borderRadius: 1, fontSize: 13.5, lineHeight: 1.6 }}>
									<Typography variant="subtitle2" sx={{ mb: 0.5 }}>רגעים שדרשו תשומת לב</Typography>
									{transcriptOf.transcript.flags.map((f, i) => (
										<div key={i} style={{ marginBottom: 6 }}>
											<span style={{ color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{moment(f.at).format('HH:mm:ss')}</span>{' '}
											<Chip size="small" label={`${KIND_LABEL[f.kind] || f.kind} ${f.severity}/3`} color={f.severity >= 3 ? 'error' : f.severity === 2 ? 'warning' : 'default'} sx={{ mx: 0.5 }} />
											<strong>{f.name}:</strong> {f.note}
											<div style={{ color: '#475569', paddingRight: 12 }}>"{f.quote}"</div>
										</div>
									))}
								</Box>
							)}
							<Typography variant="subtitle2" sx={{ mb: 0.5 }}>
								תמלול {transcriptOf.transcript.clean_lines?.length === transcriptOf.transcript.lines.length ? '(מתוקן לפי הקשר)' : '(גולמי)'}
							</Typography>
							<Box sx={{ maxHeight: 420, overflowY: 'auto', fontSize: 14, lineHeight: 1.7, border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5 }}>
								{transcriptOf.transcript.lines.map((l, i) => {
									const clean = transcriptOf.transcript?.clean_lines;
									const text = clean && clean.length === transcriptOf.transcript?.lines.length ? clean[i]?.text : l.text;
									return (
										<div key={i}>
											<span style={{ color: '#64748b', fontVariantNumeric: 'tabular-nums' }}>{moment(l.at).format('HH:mm:ss')}</span>{' '}
											<strong>{l.name}:</strong> {text}
										</div>
									);
								})}
							</Box>
						</>
					)}
				</DialogContent>
			</Dialog>

			<Dialog open={!!participantsOf} onClose={() => setParticipantsOf(null)} maxWidth="lg" fullWidth dir="rtl">
				<DialogTitle>נרשמים — {participantsOf?.circle.title?.he || participantsOf?.circle.title?.en}</DialogTitle>
				<DialogContent>
					<Typography variant="caption" color="text.secondary">הזהות האמיתית מוצגת רק כאן. במעגל עצמו כולם רואים את השם הבדוי בלבד.</Typography>
					{/* GH-841 stage C: what came in for this circle (paid, not refunded) */}
					{participantsOf && (Number(participantsOf.circle.price) || 0) > 0 && (
						<Typography variant="caption" color="text.secondary" display="block">
							{(() => {
								const paid = participantsOf.list.filter((p) => p.payment_status === 'paid');
								const cur = currencyOf(participantsOf.circle);
								// GH-841: + earlier failed refunds of the same registration (past_payments)
								const failed = participantsOf.list.reduce((sum, p) => sum + refundFailuresOf(p), 0);
								return `מחיר ${fmtMoney(Number(participantsOf.circle.price), cur)} · שילמו ${paid.length} · הכנסה ${fmtMoney(
									paid.reduce((sum, p) => sum + (Number(p.amount) || 0), 0),
									cur,
								)}${failed ? ` · ${failed} החזרים נכשלו — יש להחזיר ידנית` : ''}`;
							})()}
						</Typography>
					)}
					<TableContainer sx={{ mt: 1 }}>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell align="right">שם בדוי</TableCell>
									<TableCell align="right">מנוי/ה</TableCell>
									<TableCell align="right">אימייל</TableCell>
									<TableCell align="right">נרשם/ה</TableCell>
									<TableCell align="right">תשלום</TableCell>
									<TableCell align="right">אישור הגעה</TableCell>
									<TableCell align="right">נוכחות</TableCell>
									<TableCell align="right">הודעות</TableCell>
									<TableCell align="right">מה הביא/ה למעגל</TableCell>
									<TableCell align="right">משוב</TableCell>
									<TableCell align="right">יומן</TableCell>
									<TableCell align="right">ביטול</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{(participantsOf?.list || []).map((p) => (
									<TableRow key={p._id} sx={{ opacity: p.cancelled_at ? 0.5 : 1 }}>
										<TableCell align="right" sx={{ fontWeight: 600 }}>
											{p.display_name}
											{p.cancelled_at ? ` ${cancelReasonLabel(p.cancel_reason)}` : ''}
										</TableCell>
										<TableCell align="right">{`${p.member?.first_name || ''} ${p.member?.last_name || ''}`.trim() || '—'}</TableCell>
										<TableCell align="right">{p.member?.email || '—'}</TableCell>
										<TableCell align="right">{p.registered_at ? moment(p.registered_at).format('DD/MM HH:mm') : '—'}</TableCell>
										{/* GH-841 stage C */}
										<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
											<PaymentChip p={p} fallbackCurrency={currencyOf(participantsOf?.circle || {})} />
										</TableCell>
										{/* GH-841 stage B: 48h confirm link (informational — not confirming never loses the spot) */}
										<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{p.confirmed_at ? moment(p.confirmed_at).format('DD/MM HH:mm') : '—'}</TableCell>
										<TableCell align="right">{p.joined_at ? `נכנס/ה ${moment(p.joined_at).format('HH:mm')}${p.left_at ? ` · יצא/ה ${moment(p.left_at).format('HH:mm')}` : ' · בחדר'}` : '—'}</TableCell>
										<TableCell align="right">
											<NotifiedChips notified={p.notified} />
										</TableCell>
										{/* GH-841: reason + anonymous-share consent (the consent only exists when there is a reason) */}
										<TableCell align="right">
											{p.reason ? (
												// max-width on a <td> is ignored by the table layout, so the cap sits on an inner box
												<Box sx={{ maxWidth: 280, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
													{p.reason}
													<Box sx={{ mt: 0.5 }}>
														<Chip size="small" variant="outlined" color={p.share_reason ? 'success' : 'default'} label={p.share_reason ? 'מאשר/ת שיתוף בלי שם' : 'לא לשיתוף'} />
													</Box>
												</Box>
											) : (
												'—'
											)}
										</TableCell>
										<TableCell align="right">
											{p.feedback?.submitted_at
												? [p.feedback.rating ? `★ ${p.feedback.rating}/5` : '', p.feedback.comfort ? `נוחות לשתף ${p.feedback.comfort}/5` : '', p.feedback.comment || ''].filter(Boolean).join(' · ') || '—'
												: '—'}
										</TableCell>
										{/* GH-841 logs: everything the logs know about this participant */}
										<TableCell align="right">
											<Button size="small" startIcon={<FaHistory />} onClick={() => participantsOf && setTimelineOf({ circle: participantsOf.circle, participantId: p._id })}>
												יומן
											</Button>
										</TableCell>
										{/* GH-856: an active registration (or a payment hold) can be cancelled here — paid → full refund */}
										<TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
											{!p.cancelled_at && participantsOf && (
												<Button size="small" color="error" variant="outlined" disabled={cancellingId === p._id} onClick={() => cancelParticipant(participantsOf.circle, p)}>
													{cancellingId === p._id
														? 'מבטל…'
														: p.payment_status === 'paid'
															? `ביטול + החזר ${fmtMoney(Number(p.amount) || 0, p.currency || currencyOf(participantsOf.circle))}`
															: 'ביטול הרשמה'}
												</Button>
											)}
										</TableCell>
									</TableRow>
								))}
								{participantsOf && participantsOf.list.length === 0 && (
									<TableRow><TableCell colSpan={12} align="center"><Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>אין נרשמים.</Typography></TableCell></TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</DialogContent>
				<DialogActions><Button onClick={() => setParticipantsOf(null)}>סגירה</Button></DialogActions>
			</Dialog>

			{/* GH-841 (GH-836): topic suggestions from the members' suggestion box */}
			<SupportCircleSuggestionsDialog open={suggestionsOpen} onClose={() => setSuggestionsOpen(false)} />

			{/* GH-841 logs: the circle / participant "יומן" — mounted only while open, so each open reads once */}
			{timelineOf && (
				<SupportCircleTimeline
					key={`${timelineOf.circle._id}-${timelineOf.participantId || ''}`}
					open
					circle={timelineOf.circle}
					participantId={timelineOf.participantId || null}
					onClose={() => setTimelineOf(null)}
				/>
			)}
		</Box>
	);
}
